<script lang="ts" setup>
import { AppTypography } from '~/components/base';
import type { Candidate } from '~/types/Candidates';
import { $pluralize } from '~/utils/pluralize';

// `eager` and `priority` are separate — see PartyCard.vue: fetchpriority is a
// relative hint that only the LCP candidate (the first card) should claim.
const {
  candidate,
  eager = false,
  priority = false,
} = defineProps<{
  candidate: Candidate;
  eager?: boolean;
  priority?: boolean;
}>();
</script>

<template>
  <NuxtLink
    :to="`/candidates/${candidate.id}`"
    data-testid="candidate-card"
    class="candidate-card flex flex-col gap-4 no-underline"
  >
    <div class="flex items-center gap-2">
      <span
        class="candidate-card__avatar flex shrink-0 items-center justify-center"
      >
        <img
          v-if="candidate.image"
          :src="candidate.image"
          :alt="candidate.name"
          width="72"
          height="72"
          :loading="eager ? undefined : 'lazy'"
          :fetchpriority="priority ? 'high' : undefined"
          decoding="async"
          class="candidate-card__avatar-image size-full object-cover"
        />
        <AppTypography v-else type="subheadline-bold" class="uppercase">{{
          candidate.initials
        }}</AppTypography>
      </span>

      <div class="flex min-w-0 flex-1 flex-col gap-2">
        <span
          v-if="candidate.incumbent"
          class="candidate-card__chip inline-flex w-fit items-center"
        >
          <AppTypography type="caption-bold" class="uppercase"
            >Incumbent</AppTypography
          >
        </span>
        <div class="flex min-w-0 flex-col gap-1">
          <AppTypography
            type="body-bold"
            element="p"
            class="candidate-card__name truncate"
          >
            {{ candidate.name }}
          </AppTypography>
          <AppTypography
            type="subheadline"
            element="p"
            class="candidate-card__party truncate"
          >
            {{ candidate.party ?? 'Independent' }}
          </AppTypography>
        </div>
      </div>

      <Icon
        name="solar:alt-arrow-right-linear"
        class="candidate-card__chevron shrink-0"
      />
    </div>

    <div class="candidate-card__stats flex items-start gap-4">
      <span class="flex flex-1 items-center gap-1">
        <Icon name="solar:notes-linear" size="24" class="shrink-0" />
        <AppTypography type="title-6" class="candidate-card__stat-count">
          {{ candidate.campaignsCount }}
        </AppTypography>
        <AppTypography type="subheadline" class="candidate-card__stat-label">
          {{ $pluralize(candidate.campaignsCount, 'Campaign') }}
        </AppTypography>
      </span>
      <span class="flex flex-1 items-center gap-1">
        <Icon name="solar:buildings-linear" size="24" class="shrink-0" />
        <AppTypography type="title-6" class="candidate-card__stat-count">
          {{ candidate.lobbyistsCount }}
        </AppTypography>
        <AppTypography type="subheadline" class="candidate-card__stat-label">
          {{ $pluralize(candidate.lobbyistsCount, 'Lobbyist') }}
        </AppTypography>
      </span>
    </div>
  </NuxtLink>
</template>

<style scoped lang="scss">
.candidate-card {
  padding: $spacing-lg;
  border: 1px solid $border-color;
  border-radius: $radius-lg;
  background-color: $surface-color-bg;
  color: inherit;
  transition: box-shadow 0.15s ease;

  &:hover {
    box-shadow: 0 2px 8px rgb(0 0 0 / 6%);
  }

  &__avatar {
    width: 72px;
    height: 72px;
    overflow: hidden;
    border: 2px solid $complementary-color;
    border-radius: 9999px;
    background-color: $surface-color-bg;
    color: $text-color-primary;
  }

  &__avatar-image {
    border-radius: 9999px;
  }

  &__chip {
    padding: 2px $spacing-sm;
    border-radius: $radius-2xl;
    background-color: $complementary-color-shade-1;
    // Figma: text/primary (#201e1a) — this app's `.text-color-primary` token
    // is a different (navy) palette used elsewhere, so set the literal value.
    color: #201e1a;
  }

  &__name {
    color: $text-color-brand;
  }

  &__party {
    color: $text-color-brand-shade-1;
  }

  &__chevron {
    color: #201e1a;
  }

  &__stats {
    padding-top: $spacing-lg;
    border-top: 1px solid $border-color;
  }

  &__stat-count {
    // Figma: text/primary (#201e1a).
    color: #201e1a;
  }

  &__stat-label {
    // Figma: text/secondary (#827b71) — this app's `.text-color-tertiary`
    // token is a different (navy) palette used elsewhere, so set the
    // literal value.
    color: #827b71;
  }
}
</style>
